import { Topics } from '@app/context/constants';
import { IntervalValues, ModeValues, ProductValues, TopicValues, UpgradeProps } from '@app/routes/Pricing/Pricing';
import { BasicCompany } from '@dieterApi/company/useCompanyQuery';
import { User } from '@dieterApi/user/useUserQuery';
import { useState } from 'react';
import { animated, useSpring } from 'react-spring';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { PricingProduct, TenantVariant, TicketProduct } from './PricingTable';
import { useLocalizedTranslation } from './Translation';

type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T;
};

const topicToTicketProduct: PartialRecord<Topics, TopicValues> = {
  'topic-dse': 'dse',
  'topic-toms': 'toms',
  'topic-vvz': 'vvt',
  'topic-av': 'avv',
};

interface ProductProps {
  product: PricingProduct;
  user?: User;
  principalCompany?: BasicCompany;
  mode: ModeValues;
  interval: IntervalValues;
  queryOption?: string | null;
  showButton?: boolean;
  selectedProduct?: TicketProduct;
  onProductSelect?(val: TicketProduct | undefined): void;
  selectedTenants?: TenantVariant;
  onTenantSelect?(val: TenantVariant): void;
  locale: string;
  isWixUser?: boolean;
  onUpgrade?(props: UpgradeProps): void;
  isUpgradeHighlight?: boolean;
  upgradeDisabledReason?: string;
}

const USERCENTRICS_PRICE = {
  month: 8,
  year: 5,
};

export function ProductCardV2({
  product,
  user,
  principalCompany,
  interval,
  mode,
  queryOption,
  selectedProduct,
  onProductSelect,
  selectedTenants,
  onTenantSelect,
  locale,
  showButton = true,
  isWixUser = false,
  onUpgrade,
  isUpgradeHighlight = false,
  upgradeDisabledReason,
}: ProductProps) {
  const { t: tl } = useLocalizedTranslation(locale);
  const {
    name,
    value,
    free,
    description,
    price,
    tenantPrice,
    featureTitle,
    features,
    preferred,
    withTest,
    pricingLink,
    selectableProducts,
  } = product;

  const [withUsercentrics, setWithUsercentrics] = useState(false);
  const [featuresExpanded, setFeaturesExpanded] = useState(false);

  const isDpoProduct = mode === 'dpos';

  let displayPrice = free ? undefined : price || selectedProduct?.price;
  displayPrice =
    withUsercentrics && displayPrice
      ? {
          ...displayPrice,
          year: displayPrice?.year + USERCENTRICS_PRICE.year,
          month: displayPrice?.month + USERCENTRICS_PRICE.month,
        }
      : displayPrice;

  displayPrice =
    isDpoProduct && selectedTenants && displayPrice
      ? {
          ...displayPrice,
          year: displayPrice?.year + selectedTenants * (tenantPrice?.[selectedTenants]?.year || 0),
          month: displayPrice?.month + selectedTenants * (tenantPrice?.[selectedTenants]?.month || 0),
        }
      : displayPrice;

  const springProps = useSpring({
    val: interval !== 'month' ? displayPrice?.year : displayPrice?.month,
    from: { val: interval !== 'month' ? displayPrice?.month : displayPrice?.year },
  });

  const handleClick = () => {
    if (window.dataLayer && displayPrice) {
      window.dataLayer.push({
        event: 'ga4-event-order-checkout',
        ordervalue: displayPrice[interval] * 12 * 0.1,
        originalvalue: displayPrice[interval] * 12,
        email: user?.email,
      });
    }

    if (isSubscriber) {
      onUpgrade && onUpgrade({ product: value as ProductValues, topic: selectedProduct?.value, mode, interval });
    } else {
      window.open(pricingLink[interval] + (withUsercentrics ? '&usercentrics=true' : ''), '_blank');
    }
  };

  const handleProductSelect = (val: string) => {
    onProductSelect && onProductSelect(selectableProducts?.find((opt) => opt.value === val));
  };

  const handleTenantSelect = (val: string) => {
    onTenantSelect && onTenantSelect(Number(val) as TenantVariant);
  };

  // --- Subscription detection logic (preserved exactly) ---
  let isCurrentSubscription = false;
  let productIncluded = false;
  const isDpo = principalCompany?.isDpo || false;
  const bookedProducts =
    principalCompany?.topicsActivated.map((topic) => topicToTicketProduct[topic] as TopicValues) || [];
  const isAgency = principalCompany?.isAgency || false;
  const isPlus = principalCompany?.isPlus || false;
  const isSubscriber = user?.isSubscriber || false;
  const isTicket = (principalCompany?.subscription === 'Ticket' && isSubscriber) || false;
  const isBasic = (principalCompany?.subscription === 'Basic' && isSubscriber) || false;
  const isPremium = (principalCompany?.subscription === 'Premium' && isSubscriber && !isPlus) || false;
  const isBasicOrPremium = isBasic || isPremium || isPlus;
  let isCurrentTicket = false;
  let isUpgradable = true;

  switch (product.value) {
    case 'basic':
      switch (product.isAgency) {
        case true:
          productIncluded = isPremium && isAgency;
          isCurrentSubscription = isBasic && isAgency && !isDpo;
          isUpgradable = !isDpo;
          break;
        case false:
          switch (product.isDpo) {
            case true:
              isCurrentSubscription = isDpo && isSubscriber;
              isUpgradable = !isAgency;
              break;
            case false:
              productIncluded = ((isPremium || isPlus) && !isAgency) || isAgency;
              isCurrentSubscription = isBasic && !isAgency && !isDpo;
              break;
          }
          break;
      }
      break;
    case 'premium':
      switch (product.isAgency) {
        case true:
          isCurrentSubscription = isPremium && isAgency && !isDpo;
          isUpgradable = !isDpo;
          break;
        case false:
          productIncluded = isDpo || isAgency || isPlus;
          isCurrentSubscription = isPremium && !isAgency && !isDpo;
          break;
      }
      break;
    case 'premiumplus':
      productIncluded = isPlus;
      isCurrentSubscription = isPlus;
      break;
    case 'ticket':
      productIncluded = isBasicOrPremium;
      isCurrentTicket = bookedProducts.includes(selectedProduct?.value as TopicValues);
      isCurrentSubscription = isTicket && isCurrentTicket;
      break;
  }

  const isBlurred = ((productIncluded && !isCurrentSubscription) || !isUpgradable) && isSubscriber;
  const showCta =
    showButton &&
    ((isUpgradable && !productIncluded && (!isCurrentSubscription || (isTicket && !isCurrentTicket))) || !isSubscriber);

  const featureList = selectableProducts ? selectedProduct?.features : features;
  const visibleFeatures = featuresExpanded ? featureList : featureList?.slice(0, 5);
  const hasMoreFeatures = (featureList?.length || 0) > 5;

  const isQueryHighlighted = !!queryOption && product.value === queryOption;

  const ctaLabel = isSubscriber
    ? tl('common.upgrade_now')
    : !displayPrice
      ? tl('common.start_now')
      : withTest
        ? tl('common.try_now')
        : tl('common.book_now');

  return (
    <div
      data-testid={`pricing-card-${product.value}`}
      // Which plan the account is actually on. Exposed as an attribute rather
      // than left to the badge's label so a test can assert it without
      // depending on translated copy.
      data-current-plan={isCurrentSubscription ? 'true' : 'false'}
      // Subgrid over three parent rows (header, price/CTA, features): price and
      // CTA line up across cards and stay put when one card's features expand.
      className={`grid grid-rows-subgrid row-span-3 min-w-[280px] rounded-2xl overflow-hidden bg-pricing-bg-light-alt relative${isQueryHighlighted ? ' ring-4 ring-dieter-purple' : ''}`}
    >
      {/* Blur overlay for included/non-upgradable products */}
      {isBlurred && <div className="absolute inset-0 z-10 rounded-2xl bg-gray-300/30 backdrop-blur-[1px]" />}

      {/* Header: Current Plan / Most Popular / Subscriber status */}
      <div className="relative overflow-hidden rounded-t-2xl shrink-0">
        {isSubscriber ? (
          <div
            className={`w-full h-10 flex items-center justify-center text-xs font-bold uppercase tracking-wide ${
              isCurrentSubscription
                ? 'bg-pricing-yellow text-dieter-black'
                : isUpgradeHighlight && !productIncluded
                  ? 'bg-dieter-purple text-white'
                  : isTicket && product.value === 'basic' && mode === 'companies'
                    ? 'bg-dieter-purple text-white'
                    : ''
            }`}
          >
            {isCurrentSubscription
              ? tl('common.currentplan')
              : isUpgradeHighlight && !productIncluded
                ? tl('common.upgradeoption')
                : isTicket && product.value === 'basic' && mode === 'companies'
                  ? tl('common.recommendation')
                  : null}
          </div>
        ) : preferred ? (
          <div className="w-full h-10 flex items-center justify-center text-xs font-bold uppercase tracking-wide bg-pricing-yellow text-dieter-black">
            {tl('common.most_popular')}
          </div>
        ) : (
          <div className="h-10" aria-hidden />
        )}

        <div className="p-6">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">DIETER</p>
              <h3 className="text-xl font-bold text-dieter-black mt-1">{name}</h3>
            </div>
            {displayPrice && interval === 'year' && (
              <span className="flex-shrink-0 px-2.5 py-1 rounded text-xs font-bold uppercase bg-pricing-yellow text-dieter-black">
                {tl('common.save')} {displayPrice.discount}%
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Card Body (row spacing comes from the parent grid's gap) */}
      <div className="px-6 flex flex-col">
        {/* Price */}
        {displayPrice ? (
          <div>
            <div className="flex flex-wrap items-baseline gap-2">
              {(value === 'premium' || value === 'premiumplus') && (
                <span className="text-sm text-gray-600">{tl('common.from')}</span>
              )}
              <animated.span
                data-testid={`pricing-price-${product.value}`}
                className="text-2xl font-bold text-dieter-black"
              >
                {springProps?.val?.to((val) => (isWixUser ? val.toFixed(2) : Math.round(val)))}
              </animated.span>
              <span className="text-2xl font-bold text-dieter-black">€</span>
              <span className="text-sm text-gray-600">/ {tl('common.month')}</span>
              {interval === 'year' && (
                <span className="ml-2 text-sm text-gray-500 line-through">
                  {tl('common.instead')}: {displayPrice.month}€
                </span>
              )}
            </div>
            {product.value === 'basic' && interval === 'month' ? (
              <div className="flex flex-col gap-0.5 mt-1">
                <span className="text-xs text-gray-400 line-through">{tl('common.period')}</span>
                <span className="text-xs bg-dieter-purple text-white font-semibold rounded-lg py-1 px-2 w-fit">
                  {tl('common.period_monthly')}
                </span>
              </div>
            ) : (
              <span className="text-xs bg-pricing-yellow text-dieter-black font-semibold rounded-lg py-1 px-2 w-fit mt-1 inline-block">
                {tl('common.period')}
              </span>
            )}
          </div>
        ) : (
          <div className="h-16 flex items-center">
            <span className="text-2xl font-bold text-dieter-black">{tl('common.always_free')}</span>
          </div>
        )}

        {/* Selectable Products Dropdown */}
        {!!selectableProducts?.length && (
          <div className="relative mt-4">
            <select
              className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-3 pr-10 text-sm text-dieter-black focus:border-dieter-purple focus:outline-none focus:ring-1 focus:ring-dieter-purple"
              value={selectedProduct?.value || ''}
              onChange={(e) => handleProductSelect(e.target.value)}
            >
              <option value="" disabled>
                {tl('common.select_product')}
              </option>
              {selectableProducts.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
        )}

        {/* Tenant Selector */}
        {isDpoProduct && (
          <div className="relative mt-4">
            <select
              className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-3 pr-10 text-sm text-dieter-black focus:border-dieter-purple focus:outline-none focus:ring-1 focus:ring-dieter-purple"
              value={String(selectedTenants ?? 0)}
              onChange={(e) => handleTenantSelect(e.target.value)}
            >
              {([0, 1, 5, 10] as const).map((val) => (
                <option key={val} value={String(val)}>
                  {tl(`tenants_select.${val}`)}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
        )}

        {/* Description */}
        <p className="text-sm text-dieter-black mt-3">
          {selectableProducts ? selectedProduct?.description : description}
        </p>

        {/* Usercentrics Addon */}
        <div className="mt-auto pt-6">
          {!free && !product.isAgency && !isWixUser && !productIncluded && !isCurrentSubscription && !isSubscriber && (
            <label className="flex items-start gap-3 cursor-pointer mt-3">
              <input
                type="checkbox"
                checked={withUsercentrics}
                onChange={(e) => setWithUsercentrics(e.target.checked)}
                className="mt-1 rounded border-gray-300"
              />
              <span className="text-sm text-dieter-black">
                <span className="font-medium">{tl('common.addon_label')}</span> Professioneller Cookie-Banner zzgl.{' '}
                {USERCENTRICS_PRICE[interval]} EUR/Monat
                <span className="text-gray-400 text-xs"> (powered by Usercentrics, max. 25.000 Sessions/Month)</span>
              </span>
            </label>
          )}

          {/* CTA Button */}

          {showCta ? (
            <button
              data-testid={`pricing-cta-${product.value}`}
              onClick={handleClick}
              disabled={(!!selectableProducts?.length && !selectedProduct) || (isSubscriber && !!upgradeDisabledReason)}
              title={isSubscriber && upgradeDisabledReason ? upgradeDisabledReason : undefined}
              className={`block w-full mt-4 py-4 rounded-xl font-bold text-sm text-center text-white transition-colors whitespace-nowrap min-w-0 ${
                (!!selectableProducts?.length && !selectedProduct) || (isSubscriber && !!upgradeDisabledReason)
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-dieter-purple hover:bg-dieter-purple-hover'
              } ${displayPrice ? 'ga4-event-landing-checkout' : ''}`}
            >
              {ctaLabel}
            </button>
          ) : (
            <div className="h-[58px]" />
          )}
        </div>
      </div>

      {/* Features */}
      <div className="px-6 pb-6 min-h-[350px]">
        <p className="text-sm font-semibold text-dieter-black">{featureTitle}</p>
        <ul className="space-y-2 mt-2">
          {visibleFeatures?.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-dieter-black">
              <Check className="w-4 h-4 flex-shrink-0 mt-0.5 text-dieter-purple" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {hasMoreFeatures && (
          <button
            type="button"
            onClick={() => setFeaturesExpanded((v) => !v)}
            className="inline-flex items-center gap-1 text-sm font-medium text-dieter-black underline mt-2"
          >
            <Plus className="w-4 h-4" />
            {featuresExpanded ? tl('common.less_features') : tl('common.more_features')}
          </button>
        )}
      </div>
    </div>
  );
}
